import { EDcrCancellationReason, EDcrReason, EDcrStatus, EDcrType } from '../types';
export const DCR_ROUTES_BASE = '/v2/routes/dcr_intake';
/**
 * Store the connector should read and write for these requests. Omitting it leaves the
 * connector on Salesforce, which is what the classic data change app relies on.
 */
export const DCR_NATIVE_STORE = 'native';
/** MOM feature flag that turns this experience on, shared by the site app and Studio */
export const NATIVE_DCR_FEATURE_FLAG = 'NativeDataChangeRequests';
/**
 * Oldest connector release that serves this experience. The connector publishes its version as
 * the public org config dcr_intake__version; anything older (or missing) predates the native
 * store routes these views call.
 */
export const DCR_MINIMUM_CONNECTOR_VERSION = '1.11.4';
/** UI labels. The enum values are the strings the connector stores. */
export const DCR_TYPE_LABELS = {
    [EDcrType.UPDATE_PARTICIPANT_NUMBER]: 'Update participant number',
    [EDcrType.INACTIVATE_RESPONSE]: 'Inactivate response',
    [EDcrType.UPDATE_SITE_RESPONSE]: 'Update site response',
    [EDcrType.UPDATE_PARTICIPANT_RESPONSE]: 'Update participant response',
    [EDcrType.UPDATE_VISIT_DATE]: 'Update visit date',
    [EDcrType.INACTIVATE_PARTICIPANT]: 'Inactivate participant',
    [EDcrType.MOVE_PARTICIPANT_TO_ANOTHER_SITE]: 'Move participant to another site',
    [EDcrType.MOVE_RESPONSES_TO_ANOTHER_VISIT]: 'Move responses to another visit',
    [EDcrType.OTHER]: 'Other',
};
/**
 * Menu hints shown below each data change type in the create form's type select,
 * so a site user can pick the right type without guessing what each one means.
 */
export const DCR_TYPE_HINTS = {
    [EDcrType.UPDATE_PARTICIPANT_RESPONSE]: 'The participant entered the wrong value.',
    [EDcrType.UPDATE_SITE_RESPONSE]: 'Site staff entered the wrong value.',
    [EDcrType.UPDATE_VISIT_DATE]: 'A visit is recorded on the wrong date.',
    [EDcrType.UPDATE_PARTICIPANT_NUMBER]: "A participant's number is wrong.",
    [EDcrType.MOVE_RESPONSES_TO_ANOTHER_VISIT]: 'Users responded to the wrong visit.',
    [EDcrType.MOVE_PARTICIPANT_TO_ANOTHER_SITE]: 'A participant is at the wrong site.',
    [EDcrType.INACTIVATE_RESPONSE]: 'Remove an activity response from the study.',
    [EDcrType.INACTIVATE_PARTICIPANT]: 'Not the same as deactivating a participant.',
    [EDcrType.OTHER]: 'No other type fits.',
};
export const DCR_TYPE_OPTIONS = Object.values(EDcrType).map((type) => ({
    label: DCR_TYPE_LABELS[type],
    value: type,
    description: DCR_TYPE_HINTS[type],
}));
/**
 * Longer "when to use" guidance per data change type, shown below the type
 * select once a type is picked so a site user can confirm the right choice.
 */
export const DCR_TYPE_WHEN_TO_USE = {
    [EDcrType.UPDATE_PARTICIPANT_RESPONSE]: 'Use this when a participant entered the wrong value in Medable App for Participants, including in an eCOA. If site staff entered the value, select Update site response.',
    [EDcrType.UPDATE_SITE_RESPONSE]: 'Use this when site staff entered the wrong value in Medable for Sites. If the participant entered the value, select Update participant response.',
    [EDcrType.UPDATE_VISIT_DATE]: 'Use this when a visit is recorded on the wrong date, or the date changes. To move responses to a different visit instead, select Move responses to another visit.',
    [EDcrType.UPDATE_PARTICIPANT_NUMBER]: 'Use this when a custom participant number is entered incorrectly. This changes the identifier only.',
    [EDcrType.MOVE_RESPONSES_TO_ANOTHER_VISIT]: 'Use this when completed responses were recorded under the wrong visit. To correct a date rather than move responses, select Update visit date.',
    [EDcrType.MOVE_PARTICIPANT_TO_ANOTHER_SITE]: 'Use this when a participant is enrolled at the wrong site or transfers to another one.',
    [EDcrType.INACTIVATE_RESPONSE]: 'Use this when a single screen response was submitted in error. It inactivates one response and not the activity itself, so the activity stays available.',
    [EDcrType.INACTIVATE_PARTICIPANT]: 'Use this only to remove a participant created by mistake, along with all related study data, without deleting anything. Real participants who stop participating must be deactivated in Medable for Sites.',
    [EDcrType.OTHER]: 'Use this request type when no other type fits. Use Message Center (https://connect.medable.com/s/message-center) for requests to add or remove site users or change a participant’s email.',
};
export const DCR_REASON_OPTIONS = Object.values(EDcrReason).map((reason) => ({
    label: reason,
    value: reason,
}));
/**
 * Reader-facing labels for cancellation reasons. The enum values stay as stored
 * (they are written onto the request as its note), while the labels drop the
 * "DCR" acronym for "data change".
 */
export const DCR_CANCELLATION_REASON_LABELS = {
    [EDcrCancellationReason.ENTERED_IN_ERROR]: 'Data change entered in error',
    [EDcrCancellationReason.NO_LONGER_REQUIRED]: 'Data change no longer required',
    [EDcrCancellationReason.OTHER]: 'Other',
};
export const DCR_CANCELLATION_REASON_OPTIONS = Object.values(EDcrCancellationReason).map((reason) => ({
    label: DCR_CANCELLATION_REASON_LABELS[reason],
    value: reason,
}));
/** Statuses that freeze a request: no edit, no cancel, and no way back */
export const DCR_TERMINAL_STATUSES = [EDcrStatus.RESOLVED, EDcrStatus.DECLINED, EDcrStatus.CANCELLED];
/** Editing is also blocked once the data services team has picked the request up */
export const DCR_NON_EDITABLE_STATUSES = [...DCR_TERMINAL_STATUSES, EDcrStatus.IN_PROGRESS];
/**
 * Reader-facing labels for statuses. The enum values stay as stored (the
 * connector and existing requests key off them); only what the UI shows changes,
 * so "Open" reads as "New".
 */
export const DCR_STATUS_LABELS = {
    [EDcrStatus.OPEN]: 'New',
    [EDcrStatus.IN_PROGRESS]: 'In progress',
    [EDcrStatus.RESOLVED]: 'Resolved',
    [EDcrStatus.DECLINED]: 'Declined',
    [EDcrStatus.CANCELLED]: 'Cancelled',
};
export const DCR_STATUS_OPTIONS = Object.values(EDcrStatus).map((status) => ({
    label: DCR_STATUS_LABELS[status],
    value: status,
}));
/**
 * What a status means for the site, written onto the request when it is set. The two statuses
 * that end a request without carrying it out are missing on purpose: those read as a reason, so
 * the person setting them writes it.
 */
export const DCR_STATUS_NOTES = {
    [EDcrStatus.OPEN]: 'The request is new and awaiting review.',
    [EDcrStatus.IN_PROGRESS]: 'This data change is being implemented, so the request cannot be edited or cancelled.',
    [EDcrStatus.RESOLVED]: 'The requested change was completed.',
};
/**
 * Types the connector can carry out on its own. Anything else is worked by hand, so the execute
 * action is offered but disabled. Mirrors executableTypes in the connector's automated change
 * service, which rejects the rest anyway.
 */
export const DCR_EXECUTABLE_TYPES = [
    EDcrType.UPDATE_PARTICIPANT_NUMBER,
    EDcrType.INACTIVATE_PARTICIPANT,
    EDcrType.INACTIVATE_RESPONSE,
    EDcrType.MOVE_RESPONSES_TO_ANOTHER_VISIT,
];
/** Constrained to the page sizes the shared table supports */
export const DCR_LIST_PAGE_SIZE = 25;
/**
 * Free-text nudges: when a user describes a change in the "Other" details field, or types a
 * free-text reason, we suggest the structured option that matches these keywords.
 */
export const DCR_TYPE_SUGGESTION_KEYWORDS = {
    [EDcrType.UPDATE_PARTICIPANT_NUMBER]: ['participant number', 'participant id', 'subject number'],
    [EDcrType.INACTIVATE_RESPONSE]: ['inactivate response', 'remove response', 'duplicate response'],
    [EDcrType.UPDATE_SITE_RESPONSE]: ['site response', 'clinro', 'site entered'],
    [EDcrType.UPDATE_PARTICIPANT_RESPONSE]: ['participant response', 'epro', 'diary', 'participant entered'],
    [EDcrType.UPDATE_VISIT_DATE]: ['visit date', 'reschedule visit', 'wrong date'],
    [EDcrType.INACTIVATE_PARTICIPANT]: ['inactivate participant', 'created by mistake', 'duplicate participant'],
    [EDcrType.MOVE_PARTICIPANT_TO_ANOTHER_SITE]: ['move participant', 'transfer participant', 'another site'],
    [EDcrType.MOVE_RESPONSES_TO_ANOTHER_VISIT]: ['move response', 'wrong visit', 'another visit'],
};
export const DCR_REASON_SUGGESTION_KEYWORDS = {
    [EDcrReason.INCORRECT_ENTRY]: ['incorrect', 'wrong', 'mistake', 'typo', 'error'],
};
export const DCR_CANCELLATION_REASON_SUGGESTION_KEYWORDS = {
    [EDcrCancellationReason.ENTERED_IN_ERROR]: ['error', 'mistake', 'wrong', 'duplicate'],
    [EDcrCancellationReason.NO_LONGER_REQUIRED]: ['no longer', 'not needed', 'not required', 'obsolete'],
};
